import type {
  MeetingMinutesActionItem,
  MeetingMinutesData,
  MeetingMinutesTopic,
} from '@ai-meeting/shared'

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function normalizeTopic(item: unknown): MeetingMinutesTopic {
  if (!item || typeof item !== 'object') {
    return { title: '', overview: '', discussions: [], conclusions: [] }
  }

  const record = item as Record<string, unknown>
  return {
    title: asString(record.title),
    overview: asString(record.overview),
    discussions: asStringArray(record.discussions),
    conclusions: asStringArray(record.conclusions),
  }
}

function normalizeActionItem(item: unknown): MeetingMinutesActionItem {
  if (!item || typeof item !== 'object') {
    return { content: '', assignee: '', deadline: '', done: false }
  }

  const record = item as Record<string, unknown>
  const issueType = asString(record.issue_type)
  const assigneeId =
    typeof record.assignee_id === 'number' && Number.isFinite(record.assignee_id)
      ? record.assignee_id
      : record.assignee_id === null
        ? null
        : undefined
  return {
    content: asString(record.content),
    assignee: asString(record.assignee),
    deadline: asString(record.deadline),
    done: record.done === true,
    // Reloading the page must not quietly undo the reviewer's choice; omitted
    // when blank so "never chosen" does not become an empty name.
    ...(issueType ? { issue_type: issueType } : {}),
    ...(assigneeId !== undefined ? { assignee_id: assigneeId } : {}),
  }
}

/**
 * Defensively coerce a fetched meeting's raw `insights` field into the
 * `MeetingMinutesData` shape (`purpose`/`topics`/`action_items`) that the
 * meeting detail page now renders directly. Missing/wrong-typed fields
 * default to empty strings/arrays rather than being dropped, so a
 * malformed or partial payload never crashes the render. Returns `null`
 * when the value isn't an object at all (e.g. still `null` while the
 * meeting is pending finalization).
 */
export function normalizeSavedMeetingMinutes(value: unknown): MeetingMinutesData | null {
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  const topics = Array.isArray(record.topics) ? record.topics.map(normalizeTopic) : []
  const actionItems = Array.isArray(record.action_items)
    ? record.action_items.map(normalizeActionItem)
    : []

  return {
    purpose: asStringArray(record.purpose),
    topics,
    action_items: actionItems,
  }
}
